"use client";

import { useRef, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  Mic,
  Brain,
  Lightbulb,
  BarChart3,
  Wrench,
  Trophy,
  Flame,
  Star,
  Users,
  Zap,
} from "lucide-react";

function CardWrapper({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [shimmer, setShimmer] = useState(false);

  useEffect(() => {
    if (inView) {
      const timer = setTimeout(() => setShimmer(true), delay * 1000 + 400);
      return () => clearTimeout(timer);
    }
  }, [inView, delay]);

  return (
    <motion.div
      ref={ref}
      className={`card-shimmer ${shimmer ? "shimmer-active" : ""} group relative overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02] p-6 transition-[transform,border-color,background-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-blue-500/20 hover:bg-blue-500/[0.03] hover:shadow-lg hover:shadow-blue-500/5 ${className}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay }}
    >
      {children}
    </motion.div>
  );
}

function VoiceRoleplayPreview() {
  return (
    <div className="mt-4 rounded-lg border border-white/5 bg-[#0a0f1c] p-3">
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500/20">
          <Mic className="h-4 w-4 text-blue-400" />
        </div>
        <div>
          <div className="text-[11px] font-semibold text-white">AI-Prospect: Jan de Vries</div>
          <div className="text-[11px] text-gray-500">IT Manager — Sceptisch, budget-bewust</div>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="ml-6 rounded-md bg-white/[0.03] px-2.5 py-1.5 text-[11px] text-gray-300">
          &ldquo;Klinkt interessant, maar we hebben al een oplossing...&rdquo;
        </div>
        <div className="mr-6 rounded-md bg-blue-500/10 px-2.5 py-1.5 text-[11px] text-blue-300">
          &ldquo;Begrijpelijk. Wat zou er moeten veranderen om...&rdquo;
        </div>
      </div>
    </div>
  );
}

function DashboardPreview() {
  return (
    <div className="mt-4 rounded-lg border border-white/5 bg-[#0a0f1c] p-3">
      <div className="mb-3 grid grid-cols-4 gap-2">
        {[
          { label: "Sessies", value: "128", icon: Zap },
          { label: "Gem.", value: "84", icon: Star },
          { label: "Team", value: "12", icon: Users },
          { label: "Streak", value: "7d", icon: Flame },
        ].map((s) => (
          <div key={s.label} className="rounded-md bg-white/[0.03] p-2 text-center">
            <s.icon className="mx-auto mb-1 h-3 w-3 text-blue-400" />
            <div className="text-[11px] font-bold text-white">{s.value}</div>
            <div className="text-[11px] text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>
      {/* Mini chart */}
      <div className="flex h-8 items-end gap-1">
        {[40, 55, 45, 65, 70, 60, 75, 80, 72, 85, 78, 90].map((h, i) => (
          <div key={i} className="flex-1 rounded-sm bg-blue-500/30" style={{ height: `${h}%` }} />
        ))}
      </div>
      <div className="mt-1 text-center text-[11px] text-gray-500">
        Score trend — laatste 12 sessies
      </div>
    </div>
  );
}

function ScenarioBuilderPreview() {
  return (
    <div className="mt-4 rounded-lg border border-white/5 bg-[#0a0f1c] p-3">
      <div className="space-y-2">
        <div className="rounded-md border border-white/5 px-2.5 py-1.5">
          <div className="mb-0.5 text-[11px] text-gray-500">Persona</div>
          <div className="text-[11px] text-gray-300">CFO — 50j, formeel, data-driven</div>
        </div>
        <div className="rounded-md border border-white/5 px-2.5 py-1.5">
          <div className="mb-0.5 text-[11px] text-gray-500">Bezwaren</div>
          <div className="flex flex-wrap gap-1">
            {["Budget", "Timing", "Concurrent"].map((b) => (
              <span
                key={b}
                className="rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[11px] text-amber-400"
              >
                {b}
              </span>
            ))}
          </div>
        </div>
        <div className="rounded-md border border-white/5 px-2.5 py-1.5">
          <div className="mb-0.5 text-[11px] text-gray-500">Moeilijkheid</div>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full ${s <= 4 ? "bg-blue-500" : "bg-white/5"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function GamificationPreview() {
  return (
    <div className="mt-4 rounded-lg border border-white/5 bg-[#0a0f1c] p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Flame className="h-3 w-3 text-amber-400" />
          <span className="text-[11px] font-semibold text-white">Level 12</span>
        </div>
        <span className="text-[11px] text-gray-500">2.450 / 3.000 XP</span>
      </div>
      <div className="mb-3 h-1.5 rounded-full bg-white/5">
        <div className="h-full w-[82%] rounded-full bg-gradient-to-r from-amber-500 to-orange-500" />
      </div>
      <div className="flex justify-center gap-2">
        {["First Call", "Streak 7d", "Top 3"].map((badge) => (
          <div key={badge} className="flex flex-col items-center gap-1">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-purple-500/10">
              <Trophy className="h-3.5 w-3.5 text-purple-400" />
            </div>
            <span className="text-[11px] text-gray-500">{badge}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FeatureCards() {
  return (
    <div className="mt-14 grid gap-4 md:grid-cols-2">
      {/* Large card: Voice Roleplay */}
      <CardWrapper delay={0}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10">
            <Mic className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold">
              <em>AI Voice Roleplay</em>
            </h3>
            <p className="text-sm text-gray-300">
              Realistische gesprekken met <em>AI-prospects</em>. Nederlandse stemmen, laag latency.
            </p>
          </div>
        </div>
        <div className="hidden md:block">
          <VoiceRoleplayPreview />
        </div>
      </CardWrapper>

      {/* Large card: Manager Dashboard */}
      <CardWrapper delay={0.1}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10">
            <BarChart3 className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold">
              <em>Manager Dashboard</em>
            </h3>
            <p className="text-sm text-gray-300">
              Volledig inzicht in teamvoortgang: scores, activiteit en <em>skill gaps</em>.
            </p>
          </div>
        </div>
        <div className="hidden md:block">
          <DashboardPreview />
        </div>
      </CardWrapper>

      {/* Smaller cards */}
      <CardWrapper delay={0.2} className="md:col-span-1">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10">
            <Wrench className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold">
              <em>No-Code Scenario Builder</em>
            </h3>
            <p className="text-sm text-gray-300">
              Maak zelf trainingsscenario&apos;s. Kies de persona, bezwaren en moeilijkheidsgraad.
            </p>
          </div>
        </div>
        <div className="hidden md:block">
          <ScenarioBuilderPreview />
        </div>
      </CardWrapper>

      <CardWrapper delay={0.3} className="md:col-span-1">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10">
            <Trophy className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold">
              <em>Gamification Engine</em>
            </h3>
            <p className="text-sm text-gray-300">
              XP, badges, streaks, leaderboards. Motiveer je team dagelijks.
            </p>
          </div>
        </div>
        <div className="hidden md:block">
          <GamificationPreview />
        </div>
      </CardWrapper>

      {/* Two compact cards in a row */}
      <CardWrapper delay={0.4}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10">
            <Brain className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold">
              <em>AI Coaching Rapport</em>
            </h3>
            <p className="text-sm text-gray-300">
              Na elk gesprek een rapport: score, tijdlijnanalyse en concrete verbeterpunten met
              voorbeeldzinnen.
            </p>
          </div>
        </div>
      </CardWrapper>

      <CardWrapper delay={0.5}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10">
            <Lightbulb className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold">
              <em>Real-time Coaching</em>
            </h3>
            <p className="text-sm text-gray-300">
              Ontvang live tips tijdens het gesprek. De AI herkent patronen en geeft direct
              suggesties.
            </p>
          </div>
        </div>
      </CardWrapper>
    </div>
  );
}
